import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';

const LOGO_URL = 'https://static.readdy.ai/image/cc883008a4d7bcfeafa6a2e5816cdde1/145343b2bde13a888d3f705e3f2c3ea4.png';

const languages = [
  { code: 'en', label: 'English', short: 'EN', flag: '🇬🇧' },
  { code: 'lg', label: 'Luganda', short: 'LG', flag: '🇺🇬' },
  { code: 'fr', label: 'Français', short: 'FR', flag: '🇫🇷' },
  { code: 'ar', label: 'العربية', short: 'AR', flag: '🇸🇦' },
];

export default function Navbar() {
  const { t } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, isStaff, isAdmin, isClient, isStudent, signOut, loading: authLoading } = useAuth();

  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [aiToolsOpen, setAiToolsOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [portfolioOpen, setPortfolioOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState(i18n.language || 'en');
  const [isSigningOut, setIsSigningOut] = useState(false);

  // Mobile submenu accordion toggles
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileAiOpen, setMobileAiOpen] = useState(false);
  const [mobilePortfolioOpen, setMobilePortfolioOpen] = useState(false);

  const headerRef = useRef<HTMLElement>(null);
  const servicesRef = useRef<HTMLDivElement>(null);
  const aiToolsRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);
  const portfolioRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const isHome = location.pathname === '/';

  // Scroll listener
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
    setAiToolsOpen(false);
    setLangOpen(false);
    setPortfolioOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  // Outside click & Escape key listeners
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (servicesRef.current && !servicesRef.current.contains(target)) setServicesOpen(false);
      if (aiToolsRef.current && !aiToolsRef.current.contains(target)) setAiToolsOpen(false);
      if (langRef.current && !langRef.current.contains(target)) setLangOpen(false);
      if (portfolioRef.current && !portfolioRef.current.contains(target)) setPortfolioOpen(false);
      if (userMenuRef.current && !userMenuRef.current.contains(target)) setUserMenuOpen(false);
      
      // Close mobile drawer if clicking outside the header
      if (mobileOpen && headerRef.current && !headerRef.current.contains(target)) {
        setMobileOpen(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileOpen(false);
        setServicesOpen(false);
        setAiToolsOpen(false);
        setLangOpen(false);
        setPortfolioOpen(false);
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [mobileOpen]);

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
    setCurrentLang(code);
    setLangOpen(false);
    document.documentElement.dir = code === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = code;
  };

  const currentLangObj = languages.find(l => l.code === currentLang) || languages[0];

  const navLinkClass = `text-xs xl:text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
    scrolled || !isHome
      ? 'text-darkBlue-500 hover:text-clairBlue-400 dark:text-gray-200 dark:hover:text-clairBlue-300'
      : 'text-white hover:text-clairBlue-200'
  }`;

  const handleServiceNav = (category: string) => {
    navigate(`/services?category=${category}`);
    setServicesOpen(false);
    setMobileOpen(false);
  };

  const handleAiNav = (tab: string) => {
    navigate(`/ai-tools?tab=${tab}`);
    setAiToolsOpen(false);
    setMobileOpen(false);
  };

  const scrollToSection = (id: string) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileOpen(false);
  };

  const handleSignOut = async () => {
    if (isSigningOut) return;
    setIsSigningOut(true);
    try {
      await signOut();
    } finally {
      setIsSigningOut(false);
      setUserMenuOpen(false);
      setMobileOpen(false);
      navigate('/', { replace: true });
    }
  };

  const navBg = scrolled || !isHome
    ? 'bg-white shadow-sm border-b border-gray-100 dark:bg-[#0e1f38] dark:border-[#1a3050] dark:shadow-none'
    : 'bg-transparent';

  const isNavTransparent = !scrolled && isHome;

  const toggleBtnClass = isNavTransparent
    ? 'text-white hover:bg-white/10'
    : 'text-darkBlue-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10';

  // User initials avatar
  const userInitials = profile?.full_name
    ? profile.full_name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)
    : user?.email?.[0]?.toUpperCase() ?? '?';

  return (
    <header ref={headerRef} className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 sm:gap-3 flex-shrink-0" onClick={() => setMobileOpen(false)}>
            <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center">
              <img src={LOGO_URL} alt="BientoMart Logo" className="w-9 h-9 sm:w-10 sm:h-10 object-contain rounded-lg" />
            </div>
            <span className={`font-display text-lg sm:text-xl font-bold tracking-tight ${scrolled || !isHome ? 'text-darkBlue-500 dark:text-gray-100' : 'text-white'}`}>
              BientoMart
            </span>
          </Link>

          {/* Desktop Nav (XL / LG with responsive gaps) */}
          <nav className="hidden lg:flex items-center gap-4 xl:gap-6">
            <Link to="/" className={navLinkClass}>{t('nav.home')}</Link>

            {/* Services Dropdown */}
            <div ref={servicesRef} className="relative">
              <button
                className={`${navLinkClass} flex items-center gap-1`}
                onClick={() => { setServicesOpen(v => !v); setAiToolsOpen(false); setPortfolioOpen(false); }}
                aria-expanded={servicesOpen}
              >
                {t('nav.services')}
                <i className={`ri-arrow-down-s-line transition-transform ${servicesOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {servicesOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-white dark:bg-[#102035] rounded-xl shadow-xl border border-gray-100 dark:border-[#1e3a5f] py-2 overflow-hidden z-50">
                  <button onClick={() => handleServiceNav('digital-presence')} className="w-full text-left px-4 py-2.5 text-xs font-semibold text-darkBlue-500 dark:text-gray-200 hover:bg-clairBlue-50 dark:hover:bg-[#0d2640] hover:text-clairBlue-600 transition-colors flex items-center gap-3">
                    <div className="w-7 h-7 flex items-center justify-center bg-clairBlue-50 dark:bg-[#0d2640] rounded-lg text-clairBlue-500"><i className="ri-global-line"></i></div>
                    <span>Digital Presence</span>
                  </button>
                  <button onClick={() => handleServiceNav('business-digital-solutions')} className="w-full text-left px-4 py-2.5 text-xs font-semibold text-darkBlue-500 dark:text-gray-200 hover:bg-clairBlue-50 dark:hover:bg-[#0d2640] hover:text-clairBlue-600 transition-colors flex items-center gap-3">
                    <div className="w-7 h-7 flex items-center justify-center bg-clairBlue-50 dark:bg-[#0d2640] rounded-lg text-clairBlue-500"><i className="ri-cpu-line"></i></div>
                    <span>Business & Digital Solutions</span>
                  </button>
                  <button onClick={() => handleServiceNav('creative-professional-services')} className="w-full text-left px-4 py-2.5 text-xs font-semibold text-darkBlue-500 dark:text-gray-200 hover:bg-clairBlue-50 dark:hover:bg-[#0d2640] hover:text-clairBlue-600 transition-colors flex items-center gap-3">
                    <div className="w-7 h-7 flex items-center justify-center bg-clairBlue-50 dark:bg-[#0d2640] rounded-lg text-clairBlue-500"><i className="ri-palette-line"></i></div>
                    <span>Creative & Professional Services</span>
                  </button>
                  <button onClick={() => handleServiceNav('training-learning')} className="w-full text-left px-4 py-2.5 text-xs font-semibold text-darkBlue-500 dark:text-gray-200 hover:bg-clairBlue-50 dark:hover:bg-[#0d2640] hover:text-clairBlue-600 transition-colors flex items-center gap-3">
                    <div className="w-7 h-7 flex items-center justify-center bg-clairBlue-50 dark:bg-[#0d2640] rounded-lg text-clairBlue-500"><i className="ri-graduation-cap-line"></i></div>
                    <span>Training & Learning</span>
                  </button>
                  <button onClick={() => handleServiceNav('custom-digital-projects')} className="w-full text-left px-4 py-2.5 text-xs font-semibold text-darkBlue-500 dark:text-gray-200 hover:bg-clairBlue-50 dark:hover:bg-[#0d2640] hover:text-clairBlue-600 transition-colors flex items-center gap-3 border-t border-gray-100 dark:border-[#1a3050]">
                    <div className="w-7 h-7 flex items-center justify-center bg-darkBlue-50 dark:bg-white/10 rounded-lg text-darkBlue-600 dark:text-gray-200"><i className="ri-tools-line"></i></div>
                    <span>Custom Digital Projects</span>
                  </button>
                </div>
              )}
            </div>

            {/* AI Tools Dropdown */}
            <div ref={aiToolsRef} className="relative">
              <button
                className={`${navLinkClass} flex items-center gap-1`}
                onClick={() => { setAiToolsOpen(v => !v); setServicesOpen(false); setPortfolioOpen(false); }}
                aria-expanded={aiToolsOpen}
              >
                {t('nav.aiTools')}
                <i className={`ri-arrow-down-s-line transition-transform ${aiToolsOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {aiToolsOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white dark:bg-[#102035] rounded-xl shadow-lg border border-gray-100 dark:border-[#1e3a5f] py-2 overflow-hidden z-50">
                  <button onClick={() => handleAiNav('text')} className="w-full text-left px-5 py-3 text-sm text-darkBlue-500 dark:text-gray-200 hover:bg-clairBlue-50 dark:hover:bg-[#0d2640] hover:text-clairBlue-600 transition-colors flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-clairBlue-50 dark:bg-[#0d2640] rounded-lg text-clairBlue-400"><i className="ri-quill-pen-line"></i></div>
                    <span>{t('nav.aiTools.reviews')}</span>
                  </button>
                  <button onClick={() => handleAiNav('image')} className="w-full text-left px-5 py-3 text-sm text-darkBlue-500 dark:text-gray-200 hover:bg-clairBlue-50 dark:hover:bg-[#0d2640] hover:text-clairBlue-600 transition-colors flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-clairBlue-50 dark:bg-[#0d2640] rounded-lg text-clairBlue-400"><i className="ri-image-ai-line"></i></div>
                    <span>{t('nav.aiTools.guides')}</span>
                  </button>
                  <button onClick={() => handleAiNav('video')} className="w-full text-left px-5 py-3 text-sm text-darkBlue-500 dark:text-gray-200 hover:bg-clairBlue-50 dark:hover:bg-[#0d2640] hover:text-clairBlue-600 transition-colors flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-clairBlue-50 dark:bg-[#0d2640] rounded-lg text-clairBlue-400"><i className="ri-video-ai-line"></i></div>
                    <span>{t('nav.aiTools.landing')}</span>
                  </button>
                  <Link to="/ai-money" onClick={() => { setAiToolsOpen(false); setMobileOpen(false); }} className="w-full text-left px-5 py-3 text-sm text-darkBlue-500 dark:text-gray-200 hover:bg-clairBlue-50 dark:hover:bg-[#0d2640] hover:text-clairBlue-600 transition-colors flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-darkBlue-50 dark:bg-white/10 rounded-lg text-darkBlue-400"><i className="ri-money-dollar-circle-line"></i></div>
                    <span>Make Money</span>
                  </Link>
                </div>
              )}
            </div>

            <Link to="/about" className={navLinkClass}>{t('nav.about')}</Link>

            {/* Academy Link */}
            <Link to="/academy" className={`${navLinkClass} flex items-center gap-1`}>
              <i className="ri-graduation-cap-line"></i> Academy
            </Link>

            {/* Portfolio Dropdown */}
            <div ref={portfolioRef} className="relative">
              <button
                className={`${navLinkClass} flex items-center gap-1`}
                onClick={() => { setPortfolioOpen(v => !v); setServicesOpen(false); setAiToolsOpen(false); }}
                aria-expanded={portfolioOpen}
              >
                {t('Our Work')}
                <i className={`ri-arrow-down-s-line transition-transform ${portfolioOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {portfolioOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white dark:bg-[#102035] rounded-xl shadow-lg border border-gray-100 dark:border-[#1e3a5f] py-2 overflow-hidden z-50">
                  <Link to="/portfolio" onClick={() => { setPortfolioOpen(false); setMobileOpen(false); }} className="w-full text-left px-5 py-3 text-sm text-darkBlue-500 dark:text-gray-200 hover:bg-clairBlue-50 dark:hover:bg-[#0d2640] hover:text-clairBlue-600 transition-colors flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-clairBlue-50 dark:bg-[#0d2640] rounded-lg text-clairBlue-400"><i className="ri-briefcase-line"></i></div>
                    <span>{t('Our Work')}</span>
                  </Link>
                  <Link to="/our-work-demo" onClick={() => { setPortfolioOpen(false); setMobileOpen(false); }} className="w-full text-left px-5 py-3 text-sm text-darkBlue-500 dark:text-gray-200 hover:bg-clairBlue-50 dark:hover:bg-[#0d2640] hover:text-clairBlue-600 transition-colors flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-clairBlue-50 dark:bg-[#0d2640] rounded-lg text-clairBlue-400"><i className="ri-folder-open-line"></i></div>
                    <span>Our work demo</span>
                  </Link>
                </div>
              )}
            </div>

            <button onClick={() => scrollToSection('contact')} className={navLinkClass}>{t('nav.contact')}</button>
          </nav>

          {/* Right Side */}
          <div className="hidden lg:flex items-center gap-2.5 xl:gap-3 flex-shrink-0">
            {/* Language Selector */}
            <div ref={langRef} className="relative">
              <button
                onClick={() => setLangOpen(v => !v)}
                aria-label="Select Language"
                className={`flex items-center gap-1 text-xs xl:text-sm font-medium transition-colors px-2 py-1.5 rounded-lg ${
                  scrolled || !isHome ? 'text-darkBlue-500 hover:text-clairBlue-400 dark:text-gray-300' : 'text-white hover:text-clairBlue-200'
                }`}
              >
                <span>{currentLangObj.flag}</span>
                <span>{currentLangObj.short}</span>
                <i className={`ri-arrow-down-s-line transition-transform text-xs ${langOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-2 w-44 bg-white dark:bg-[#102035] rounded-xl shadow-lg border border-gray-100 dark:border-[#1e3a5f] py-2 z-50">
                  {languages.map(lang => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-3 ${
                        currentLang === lang.code ? 'bg-clairBlue-50 dark:bg-[#0d2640] text-clairBlue-600 font-medium' : 'text-darkBlue-500 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/5'
                      }`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.label}</span>
                      {currentLang === lang.code && <i className="ri-check-line ml-auto text-clairBlue-400"></i>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Dark / Light Toggle */}
            <button
              onClick={toggleTheme}
              aria-label="Toggle Theme"
              title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all cursor-pointer ${toggleBtnClass}`}
            >
              <i className={`text-base xl:text-lg ${theme === 'dark' ? 'ri-sun-line text-amber-400' : 'ri-moon-line'}`}></i>
            </button>

            {/* Auth: user avatar dropdown OR sign-in buttons OR loading placeholder */}
            {authLoading ? (
              <div className="w-8 h-8 rounded-full bg-clairBlue-400/20 animate-pulse border border-clairBlue-400/30" />
            ) : user ? (
              <div ref={userMenuRef} className="relative">
                <button
                  onClick={() => setUserMenuOpen(v => !v)}
                  className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                  aria-label="User Account Menu"
                >
                  <div className="w-8 h-8 rounded-full bg-clairBlue-500 flex items-center justify-center text-white text-xs font-bold shadow-sm">
                    {userInitials}
                  </div>
                  <i className={`ri-arrow-down-s-line text-xs transition-transform ${userMenuOpen ? 'rotate-180' : ''} ${scrolled || !isHome ? 'text-darkBlue-500 dark:text-gray-200' : 'text-white'}`}></i>
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-[#102035] rounded-xl shadow-xl border border-gray-100 dark:border-[#1e3a5f] py-2 z-50">
                    <div className="px-4 py-2.5 border-b border-gray-100 dark:border-[#1a3050]">
                      <p className="text-xs font-bold text-darkBlue-500 dark:text-gray-100 truncate">{profile?.full_name || 'My Account'}</p>
                      <p className="text-[11px] text-gray-400 truncate">{user.email}</p>
                    </div>

                    {/* Dynamic Role Navigation */}
                    {(isStudent || isAdmin || isStaff) && (
                      <Link to="/academy/dashboard" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-xs font-semibold text-darkBlue-500 dark:text-gray-200 hover:bg-clairBlue-50 dark:hover:bg-[#0d2640] transition-colors">
                        <i className="ri-graduation-cap-line text-clairBlue-400 text-sm"></i> My Learning & Courses
                      </Link>
                    )}

                    {(isClient || isAdmin || isStaff) && (
                      <Link to="/portal" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-xs font-semibold text-darkBlue-500 dark:text-gray-200 hover:bg-clairBlue-50 dark:hover:bg-[#0d2640] transition-colors">
                        <i className="ri-briefcase-line text-clairBlue-400 text-sm"></i> Client Portal Hub
                      </Link>
                    )}

                    {isStaff && (
                      <Link to="/admin" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-xs font-semibold text-darkBlue-500 dark:text-gray-200 hover:bg-clairBlue-50 dark:hover:bg-[#0d2640] transition-colors">
                        <i className="ri-shield-star-line text-amber-500 text-sm"></i> Admin Center
                      </Link>
                    )}

                    <Link to="/profile" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-xs font-semibold text-darkBlue-500 dark:text-gray-200 hover:bg-clairBlue-50 dark:hover:bg-[#0d2640] transition-colors">
                      <i className="ri-user-settings-line text-clairBlue-400 text-sm"></i> Profile Settings
                    </Link>

                    <div className="border-t border-gray-100 dark:border-[#1a3050] mt-1 pt-1">
                      <button
                        onClick={handleSignOut}
                        disabled={isSigningOut}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors disabled:opacity-50"
                      >
                        <i className="ri-logout-box-line text-sm"></i>
                        <span>{isSigningOut ? 'Signing out...' : 'Sign Out'}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link
                  to="/login"
                  className={`text-xs xl:text-sm font-semibold px-3.5 py-2 rounded-lg transition-all ${
                    isNavTransparent ? 'text-white hover:bg-white/10' : 'text-darkBlue-500 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/10'
                  }`}
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="bg-darkBlue-500 hover:bg-darkBlue-600 text-white text-xs xl:text-sm font-semibold px-4 xl:px-5 py-2.5 rounded-lg transition-all hover:scale-105 whitespace-nowrap shadow-sm"
                >
                  Get Started
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Hamburger Button with comfortable touch target */}
          <button
            className={`lg:hidden w-11 h-11 flex items-center justify-center rounded-xl transition-colors ${
              scrolled || !isHome ? 'text-darkBlue-500 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/10' : 'text-white hover:bg-white/10'
            }`}
            onClick={() => setMobileOpen(v => !v)}
            aria-label={mobileOpen ? 'Close Navigation Menu' : 'Open Navigation Menu'}
            aria-expanded={mobileOpen}
          >
            <i className={`text-2xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Drawer Navigation */}
      {mobileOpen && (
        <div className="lg:hidden bg-white dark:bg-[#0e1f38] border-t border-gray-100 dark:border-[#1a3050] max-h-[calc(100vh-4rem)] overflow-y-auto px-4 py-5 space-y-2 shadow-2xl">
          <Link
            to="/"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-3 px-3.5 py-3 text-sm font-semibold text-darkBlue-500 dark:text-gray-100 rounded-xl hover:bg-clairBlue-50 dark:hover:bg-[#102035]"
          >
            <i className="ri-home-4-line text-clairBlue-500 text-base"></i>
            <span>{t('nav.home')}</span>
          </Link>

          {/* Services Accordion */}
          <div className="border border-gray-100 dark:border-[#1e3a5f] rounded-2xl overflow-hidden">
            <button
              onClick={() => setMobileServicesOpen(v => !v)}
              className="w-full flex items-center justify-between px-3.5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-300 bg-gray-50/70 dark:bg-[#102035]"
            >
              <span className="flex items-center gap-2">
                <i className="ri-apps-2-line text-clairBlue-500"></i>
                {t('nav.services')}
              </span>
              <i className={`ri-arrow-down-s-line transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {mobileServicesOpen && (
              <div className="p-2 space-y-1 bg-white dark:bg-[#0b1929]">
                <button onClick={() => handleServiceNav('digital-presence')} className="w-full text-left px-3 py-2.5 text-xs font-medium text-darkBlue-500 dark:text-gray-200 rounded-lg hover:bg-clairBlue-50 dark:hover:bg-[#102035] flex items-center gap-2">
                  <i className="ri-global-line text-clairBlue-400"></i>Digital Presence
                </button>
                <button onClick={() => handleServiceNav('business-digital-solutions')} className="w-full text-left px-3 py-2.5 text-xs font-medium text-darkBlue-500 dark:text-gray-200 rounded-lg hover:bg-clairBlue-50 dark:hover:bg-[#102035] flex items-center gap-2">
                  <i className="ri-cpu-line text-clairBlue-400"></i>Business & Digital Solutions
                </button>
                <button onClick={() => handleServiceNav('creative-professional-services')} className="w-full text-left px-3 py-2.5 text-xs font-medium text-darkBlue-500 dark:text-gray-200 rounded-lg hover:bg-clairBlue-50 dark:hover:bg-[#102035] flex items-center gap-2">
                  <i className="ri-palette-line text-clairBlue-400"></i>Creative & Professional Services
                </button>
                <button onClick={() => handleServiceNav('training-learning')} className="w-full text-left px-3 py-2.5 text-xs font-medium text-darkBlue-500 dark:text-gray-200 rounded-lg hover:bg-clairBlue-50 dark:hover:bg-[#102035] flex items-center gap-2">
                  <i className="ri-graduation-cap-line text-clairBlue-400"></i>Training & Learning
                </button>
                <button onClick={() => handleServiceNav('custom-digital-projects')} className="w-full text-left px-3 py-2.5 text-xs font-medium text-darkBlue-500 dark:text-gray-200 rounded-lg hover:bg-clairBlue-50 dark:hover:bg-[#102035] flex items-center gap-2">
                  <i className="ri-tools-line text-clairBlue-400"></i>Custom Digital Projects
                </button>
              </div>
            )}
          </div>

          {/* AI Tools Accordion */}
          <div className="border border-gray-100 dark:border-[#1e3a5f] rounded-2xl overflow-hidden">
            <button
              onClick={() => setMobileAiOpen(v => !v)}
              className="w-full flex items-center justify-between px-3.5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-300 bg-gray-50/70 dark:bg-[#102035]"
            >
              <span className="flex items-center gap-2">
                <i className="ri-magic-line text-clairBlue-500"></i>
                {t('nav.aiTools')}
              </span>
              <i className={`ri-arrow-down-s-line transition-transform ${mobileAiOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {mobileAiOpen && (
              <div className="p-2 space-y-1 bg-white dark:bg-[#0b1929]">
                <button onClick={() => handleAiNav('text')} className="w-full text-left px-3 py-2.5 text-xs font-medium text-darkBlue-500 dark:text-gray-200 rounded-lg hover:bg-clairBlue-50 dark:hover:bg-[#102035] flex items-center gap-2">
                  <i className="ri-quill-pen-line text-clairBlue-400"></i>{t('nav.aiTools.reviews')}
                </button>
                <button onClick={() => handleAiNav('image')} className="w-full text-left px-3 py-2.5 text-xs font-medium text-darkBlue-500 dark:text-gray-200 rounded-lg hover:bg-clairBlue-50 dark:hover:bg-[#102035] flex items-center gap-2">
                  <i className="ri-image-ai-line text-clairBlue-400"></i>{t('nav.aiTools.guides')}
                </button>
                <button onClick={() => handleAiNav('video')} className="w-full text-left px-3 py-2.5 text-xs font-medium text-darkBlue-500 dark:text-gray-200 rounded-lg hover:bg-clairBlue-50 dark:hover:bg-[#102035] flex items-center gap-2">
                  <i className="ri-video-ai-line text-clairBlue-400"></i>{t('nav.aiTools.landing')}
                </button>
                <Link to="/ai-money" onClick={() => setMobileOpen(false)} className="w-full text-left px-3 py-2.5 text-xs font-medium text-darkBlue-500 dark:text-gray-200 rounded-lg hover:bg-clairBlue-50 dark:hover:bg-[#102035] flex items-center gap-2">
                  <i className="ri-money-dollar-circle-line text-emerald-500"></i>Make Money
                </Link>
              </div>
            )}
          </div>

          <Link
            to="/about"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-3 px-3.5 py-3 text-sm font-semibold text-darkBlue-500 dark:text-gray-100 rounded-xl hover:bg-clairBlue-50 dark:hover:bg-[#102035]"
          >
            <i className="ri-information-line text-clairBlue-500 text-base"></i>
            <span>{t('nav.about')}</span>
          </Link>

          {/* Academy (Mobile) */}
          <Link
            to="/academy"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-3 px-3.5 py-3 text-sm font-semibold text-darkBlue-500 dark:text-gray-100 rounded-xl hover:bg-clairBlue-50 dark:hover:bg-[#102035]"
          >
            <i className="ri-graduation-cap-line text-clairBlue-500 text-base"></i>
            <span>Academy & Courses</span>
          </Link>

          {/* Portfolio Accordion */}
          <div className="border border-gray-100 dark:border-[#1e3a5f] rounded-2xl overflow-hidden">
            <button
              onClick={() => setMobilePortfolioOpen(v => !v)}
              className="w-full flex items-center justify-between px-3.5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-300 bg-gray-50/70 dark:bg-[#102035]"
            >
              <span className="flex items-center gap-2">
                <i className="ri-briefcase-line text-clairBlue-500"></i>
                {t('Our Work')}
              </span>
              <i className={`ri-arrow-down-s-line transition-transform ${mobilePortfolioOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {mobilePortfolioOpen && (
              <div className="p-2 space-y-1 bg-white dark:bg-[#0b1929]">
                <Link to="/portfolio" onClick={() => setMobileOpen(false)} className="w-full text-left px-3 py-2.5 text-xs font-medium text-darkBlue-500 dark:text-gray-200 rounded-lg hover:bg-clairBlue-50 dark:hover:bg-[#102035] flex items-center gap-2">
                  <i className="ri-briefcase-line text-clairBlue-400"></i>Portfolio Showcase
                </Link>
                <Link to="/our-work-demo" onClick={() => setMobileOpen(false)} className="w-full text-left px-3 py-2.5 text-xs font-medium text-darkBlue-500 dark:text-gray-200 rounded-lg hover:bg-clairBlue-50 dark:hover:bg-[#102035] flex items-center gap-2">
                  <i className="ri-folder-open-line text-clairBlue-400"></i>Live System Demos
                </Link>
              </div>
            )}
          </div>

          <button
            onClick={() => scrollToSection('contact')}
            className="w-full text-left flex items-center gap-3 px-3.5 py-3 text-sm font-semibold text-darkBlue-500 dark:text-gray-100 rounded-xl hover:bg-clairBlue-50 dark:hover:bg-[#102035]"
          >
            <i className="ri-mail-line text-clairBlue-500 text-base"></i>
            <span>{t('nav.contact')}</span>
          </button>

          {/* Authenticated / Public Mobile Menu */}
          <div className="pt-2 border-t border-gray-100 dark:border-[#1a3050]">
            {authLoading ? (
              <div className="h-14 bg-gray-50 dark:bg-[#102035] rounded-2xl animate-pulse" />
            ) : user ? (
              <div className="space-y-1 bg-gray-50 dark:bg-[#102035] p-3 rounded-2xl border border-gray-100 dark:border-[#1e3a5f]">
                <div className="px-2 py-1.5 flex items-center gap-3 border-b border-gray-200/60 dark:border-[#1a3050] pb-2.5 mb-2">
                  <div className="w-9 h-9 rounded-full bg-clairBlue-500 flex items-center justify-center text-white text-xs font-bold shadow-sm">
                    {userInitials}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-darkBlue-500 dark:text-gray-100 truncate">{profile?.full_name || 'My Account'}</p>
                    <p className="text-[11px] text-gray-400 truncate">{user.email}</p>
                  </div>
                </div>

                {(isStudent || isAdmin || isStaff) && (
                  <Link to="/academy/dashboard" onClick={() => setMobileOpen(false)} className="w-full text-left px-3 py-2.5 text-xs font-semibold text-darkBlue-500 dark:text-gray-200 rounded-xl hover:bg-white dark:hover:bg-[#0b1929] flex items-center gap-2.5 transition-colors">
                    <i className="ri-graduation-cap-line text-clairBlue-400"></i> My Learning & Courses
                  </Link>
                )}

                {(isClient || isAdmin || isStaff) && (
                  <Link to="/portal" onClick={() => setMobileOpen(false)} className="w-full text-left px-3 py-2.5 text-xs font-semibold text-darkBlue-500 dark:text-gray-200 rounded-xl hover:bg-white dark:hover:bg-[#0b1929] flex items-center gap-2.5 transition-colors">
                    <i className="ri-briefcase-line text-clairBlue-400"></i> Client Portal Hub
                  </Link>
                )}

                {isStaff && (
                  <Link to="/admin" onClick={() => setMobileOpen(false)} className="w-full text-left px-3 py-2.5 text-xs font-semibold text-darkBlue-500 dark:text-gray-200 rounded-xl hover:bg-white dark:hover:bg-[#0b1929] flex items-center gap-2.5 transition-colors">
                    <i className="ri-shield-star-line text-amber-500"></i> Admin Operations Center
                  </Link>
                )}

                <Link to="/profile" onClick={() => setMobileOpen(false)} className="w-full text-left px-3 py-2.5 text-xs font-semibold text-darkBlue-500 dark:text-gray-200 rounded-xl hover:bg-white dark:hover:bg-[#0b1929] flex items-center gap-2.5 transition-colors">
                  <i className="ri-user-settings-line text-clairBlue-400"></i> Profile Settings
                </Link>

                <div className="pt-2 border-t border-gray-200/60 dark:border-[#1a3050]">
                  <button
                    onClick={handleSignOut}
                    disabled={isSigningOut}
                    className="w-full text-left px-3 py-2.5 text-xs font-bold text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-xl flex items-center gap-2.5 transition-colors disabled:opacity-50"
                  >
                    <i className="ri-logout-box-line text-sm"></i>
                    <span>{isSigningOut ? 'Signing out...' : 'Sign Out Securely'}</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-2 pt-1">
                <Link
                  to="/login"
                  onClick={() => setMobileOpen(false)}
                  className="w-full text-center px-4 py-3 text-xs font-bold text-darkBlue-500 dark:text-gray-200 border border-gray-200 dark:border-[#1e3a5f] rounded-xl hover:bg-gray-50 dark:hover:bg-white/5"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  onClick={() => setMobileOpen(false)}
                  className="w-full text-center bg-darkBlue-500 hover:bg-darkBlue-600 text-white text-xs font-bold px-5 py-3 rounded-xl shadow-md"
                >
                  Get Started Free
                </Link>
              </div>
            )}
          </div>

          {/* Language + theme toggles */}
          <div className="pt-3 border-t border-gray-100 dark:border-[#1a3050] space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              {languages.map(lang => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={`flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-semibold border transition-colors cursor-pointer ${
                    currentLang === lang.code
                      ? 'border-clairBlue-300 bg-clairBlue-50 text-clairBlue-600 dark:bg-[#0d2640] dark:border-clairBlue-400'
                      : 'border-gray-200 dark:border-[#1e3a5f] text-darkBlue-500 dark:text-gray-300'
                  }`}
                >
                  <span>{lang.flag}</span>
                  <span>{lang.short}</span>
                </button>
              ))}

              <button
                onClick={toggleTheme}
                aria-label="Toggle Dark/Light Mode"
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border border-gray-200 dark:border-[#1e3a5f] text-darkBlue-500 dark:text-gray-300 transition-colors cursor-pointer"
              >
                <i className={`text-sm ${theme === 'dark' ? 'ri-sun-line text-amber-400' : 'ri-moon-line text-clairBlue-400'}`}></i>
                <span>{theme === 'dark' ? 'Light' : 'Dark'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

